import {
  createLicenceOutTable,
  LicenceOutTable,
} from './account/account.models.js'
import {
  createOptimizationSavingsTable,
  createOptimizationTable,
  OptimizationSavingsTable,
  OptimizationTable,
} from './optimization/optimization.models.js'
import { SQLocalKysely } from 'sqlocal/kysely'
import { Kysely, LogEvent } from 'kysely'
import { sql } from 'kysely'
import {
  CacheStateRepository,
  CacheStateTable,
  createCacheStateTable,
} from './cacheState.store.js'
import {
  createApplicationCostsTable,
  createApplicationTable,
  createApplicationUserCountTable,
  ApplicationCostsTable,
  ApplicationTable,
  ApplicationUserCountTable,
} from '@/stores/application/application.models.js'
import { AccountStore } from '@/stores/account/account.store.js'
import { ApplicationStore } from '@/stores/application/application.store.js'
import { OptimizationStore } from '@/stores/optimization/optimization.store.js'
import {
  createPersonTable,
  PersonTable,
} from '@/stores/person/person.models.js'
import { PersonStore } from '@/stores/person/person.store.js'
import {
  BrowserDeploymentTable,
  BrowserLastUsageTable,
  createBrowserDeploymentTable,
  createBrowserLastUsageTable,
} from './browserextension/browserextension.models.js'
import { BrowserExtensionStore } from './browserextension/browserextension.store.js'
import { logError } from '@/utils/errors.js'

export const DB_VERSION = 7

export interface Database {
  licence_out: LicenceOutTable
  cache: CacheStateTable
  application: ApplicationTable
  application_usercount: ApplicationUserCountTable
  application_costs: ApplicationCostsTable
  optimization: OptimizationTable
  optimization_savings: OptimizationSavingsTable
  person: PersonTable
  browser_last_usage: BrowserLastUsageTable
  browser_deployment: BrowserDeploymentTable
}

const { dialect } = new SQLocalKysely('database.sqlite3')

const logKyselyEvent = (event: LogEvent) => {
  if (event.level === 'error') {
    logError(
      event.error,
      'Kysely query failed',
      {
        sql: { text: event.query.sql, params: event.query.parameters },
        timing: { ms: event.queryDurationMillis },
      },
      { op: 'kysely.query' }
    )
  }
}

export const db = new Kysely<Database>({ dialect, log: logKyselyEvent })

export async function configureSQLite(db: Kysely<Database>) {
  await sql`PRAGMA synchronous = OFF;`.execute(db)
  await sql`PRAGMA journal_mode = MEMORY;`.execute(db)
  await sql`PRAGMA locking_mode = EXCLUSIVE;`.execute(db)
  await sql`PRAGMA foreign_keys = OFF;`.execute(db)
  await sql`PRAGMA temp_store = MEMORY;`.execute(db)
  await sql`PRAGMA cache_size = 114176;`.execute(db)
  await sql`PRAGMA SQLITE_MAX_SQL_LENGTH = 1073741824;`.execute(db)
  await sql`PRAGMA journal_size_limit = 6144000;`.execute(db)
}

createLicenceOutTable().catch((err) => {
  console.error('Failed to create table:', err)
})

createCacheStateTable().catch((err) => {
  console.error('Failed to create Metadata Cache table', err)
})

createApplicationCostsTable().catch((err) => {
  console.error('Failed to create Application Costs Cache', err)
})

createApplicationUserCountTable().catch((err) => {
  console.error('Failed to create Application User Count Cache', err)
})

createApplicationTable().catch((err) => {
  console.error('Failed to create Application Cache', err)
})

createOptimizationSavingsTable().catch((err) => {
  console.error('Failed to create Optimization Savings Cache', err)
})

createOptimizationTable().catch((err) => {
  console.error('Failed to create Optimization Cache', err)
})

createPersonTable().catch((err) => {
  console.error('Failed to create Person table', err)
})

createBrowserLastUsageTable().catch((err) => {
  console.error('Failed to create Browser Last Usage Cache', err)
})

createBrowserDeploymentTable().catch((err) => {
  console.error('Failed to create Browser Deployment Cache', err)
})

// Function to reset the database and recreate tables
export async function resetDatabase() {
  await ApplicationStore.reset()
  await AccountStore.reset()
  await BrowserExtensionStore.reset()
  await OptimizationStore.reset()
  await PersonStore.reset()

  await sql`DELETE FROM cache`.execute(db)
}

export async function updateAllCaches(fullReload: boolean = false) {
  await updateDeltaCaches(fullReload)

  const deltaTimestamps = {
    account: AccountStore.lastCacheChange.value,
    app: ApplicationStore.lastCacheChange.value,
    optimization: OptimizationStore.lastCacheChange.value,
    person: PersonStore.lastCacheChange.value,
  }

  const costTimestamps = {
    app: ApplicationStore.lastCostsCacheChange.value,
    optimization: OptimizationStore.lastSavingsCacheChange.value,
  }

  const costValues = Object.values(costTimestamps)
  const deltaValues = Object.values(deltaTimestamps)

  const shouldPreloadCosts = costValues.some((t) => !t)
  const isCostUpdateNeeded = deltaValues.some(
    (delta) => delta && costValues.every((cost) => cost && delta > cost)
  )

  if (fullReload || shouldPreloadCosts || isCostUpdateNeeded) {
    await updateCostCaches()
  }

  await BrowserExtensionStore.updateBrowserDeploymentCacheFromApi()
}

export async function updateCostCaches() {
  await Promise.all([
    OptimizationStore.updateSavingsCacheFromApi(),
    ApplicationStore.updateApplicationCostsCacheFromApi(),
  ])
}

export async function updateDeltaCaches(fullReload: boolean = false) {
  await Promise.all([
    AccountStore.updateAccountCacheFromApi(fullReload),
    ApplicationStore.updateApplicationCacheFromApi(fullReload),
    ApplicationStore.updateApplicationUserCountCacheFromApi(),
    OptimizationStore.updateOptimizationCacheFromApi(fullReload),
    PersonStore.updatePersonCacheFromApi(fullReload),
    BrowserExtensionStore.updateBrowserLastUsageCacheFromApi(fullReload),
  ])
}

export async function ensureSchemaVersion() {
  const [{ user_version: currentVersion }] = (
    await sql`PRAGMA user_version;`.execute(db)
  ).rows as { user_version: number }[]

  if (currentVersion !== DB_VERSION) {
    await resetDatabase()
    await sql`PRAGMA user_version = ${sql.raw(String(DB_VERSION))};`.execute(db)
  }
}

export async function ensureCompanyCacheScope(companyId: string) {
  const cacheCompany = await CacheStateRepository.getCacheStateById('companyId')

  if (cacheCompany?.companyId && cacheCompany.companyId !== companyId) {
    await resetDatabase()
  }

  await CacheStateRepository.upsertCacheState({
    id: 'companyId',
    companyId,
  })
}
